import type {
  RafBreakdown,
  RafScore,
  RafScoreResponse,
} from '@seen/request-types/rafScore'
// oxlint-disable-next-line no-restricted-imports
import { useQuery } from '@tanstack/react-query'
import { useContext } from 'react'
import { SeenApiContext } from '#src/contexts/seen-api/SeenApiContext.tsx'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'

interface UseRafBreakdownResult {
  rafBreakdown: RafBreakdown | null
  rafBreakdownV22: RafBreakdown | null
  rafScore: RafScore | null
  isPending: boolean
}

export function useRafBreakdown(
  patientId: string | undefined
): UseRafBreakdownResult {
  // Read optionally rather than through useSeenApi(), which throws when no
  // provider is present: the AI capability is optional and some hosts of these
  // participant surfaces supply no SeenApiContext at all. Matches useRafScore.
  const ai = useContext(SeenApiContext)?.ai
  const canQuery = Boolean(ai && patientId)

  const { data, isPending } = useQuery({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.rafScore(patientId, true),
    queryFn: async (): Promise<RafScoreResponse | null> => {
      if (!ai || !patientId) {
        return null
      }
      return ai.rafScore({ patientId, includeBreakdown: true })
    },
    enabled: canQuery,
  })

  return {
    rafBreakdown: data?.rafBreakdown ?? null,
    rafBreakdownV22: data?.rafBreakdownV22 ?? null,
    rafScore: data?.rafScore ?? null,
    isPending: canQuery && isPending,
  }
}
